import React, {useRef} from "react";
import {useMessages} from "i18n";
import {ProPill} from "home/components/UpgradeHints";
import {Actions, CardMain, CardText, GhostButton, SaveButton} from "../components/AccountCards";
import {useAccountBrand} from "../hooks/useAccountBrand";
import {LOGO_ACCEPT_ATTRIBUTE} from "./brandValidation";
import BrandColorPalette from "./BrandColorPalette";
import {BRAND_PALETTE} from "./brandPalette";
import {
	ColorValue,
	FieldLabel,
	Hint,
	HiddenFileInput,
	LockedNote,
	LogoFrame,
	PreviewBar,
	Row,
} from "./BrandSettings.styles";
import {Section, SectionTitle} from "./BrandSheet.styles";

/**
 * The brand editor itself (api#98) — the "Your brand" SECTION of the personalization sheet. Its ONE
 * caller is `BrandSheet`, the top-bar item framed as the shared non-modal card sheet. The account → Profile
 * card that used to render it too was removed in ui#495: it has a single home now, so there is no
 * second surface to drift from.
 *
 * It supplies its own heading since ui#529. It used to borrow the sheet's, which worked while the sheet
 * held nothing else and stopped working the moment it held three things — the sheet is "Personalization"
 * now, and this is one of the things inside it.
 *
 * Presentational only: the entitlement gate, validation and the four `/account/brand` calls live in
 * {@link useAccountBrand} (epic #26), so both surfaces inherit one gating decision.
 *
 * It rendered the download-page theme editor as a sibling until ui#648; that editor is now a TAB of the
 * same sheet ({@link ./BrandSheet}), which is still one home — the "single home" argument from ui#495 is
 * about surfaces a sender must go looking for, not about panels inside one of them.
 *
 * Two things here are contract, not styling:
 * 1. The preview paints the accent as a BACKGROUND with the server-derived foreground on top, which is
 *    the only usage the WCAG guarantee covers.
 * 2. A non-entitled sender sees no editor at all, never a disabled-looking one — the api rejects their
 *    writes anyway, so exposing controls would only produce 400s.
 */
export type BrandSettingsProps = {
	/**
	 * Whether THIS section renders the sheet's single "Available on Premium." note. The sheet decides
	 * ({@link brandSheetUpsell}) because its sections share that one sentence: a section that answered
	 * for itself would put it on screen twice for a FREE sender, which is both noise and a Playwright
	 * strict-mode failure. Separate from `entitled` below on purpose — that one still decides whether
	 * the EDITOR renders, and only this one decides who explains why it did not.
	 */
	showLockedNote: boolean;
};

export default function BrandSettings({showLockedNote}: BrandSettingsProps) {
	const m = useMessages();
	const {entitled, brand, color, onColor, busy, canSave, canReset, setColor, saveColor, uploadLogo, reset} =
		useAccountBrand();
	const filePicker = useRef<HTMLInputElement>(null);

	return (
		<Section>
			<SectionTitle>{m.account.brand.title}</SectionTitle>
			<CardText>{m.account.brand.description}</CardText>

			{!entitled && showLockedNote && (
				<LockedNote>
					<ProPill aria-label={m.account.brand.lockedAria}>{m.pricing.planNames.premium}</ProPill>
					<Hint>{m.account.brand.lockedHint}</Hint>
				</LockedNote>
			)}

			{entitled && (
				<CardMain>
					<Row>
						<FieldLabel id="brand-logo-label">{m.account.brand.logoLabel}</FieldLabel>
						<LogoFrame>
							{brand.logoUrl && <img src={brand.logoUrl} alt={m.account.brand.logoAlt}/>}
						</LogoFrame>
						<GhostButton
							disabled={busy}
							aria-describedby="brand-logo-label"
							onClick={() => filePicker.current?.click()}
						>
							{brand.logoUrl ? m.account.brand.replaceLogo : m.account.brand.chooseLogo}
						</GhostButton>
						<HiddenFileInput
							ref={filePicker}
							type="file"
							accept={LOGO_ACCEPT_ATTRIBUTE}
							aria-label={m.account.brand.logoLabel}
							onChange={(event) => {
								const picked = event.target.files?.[0];
								// Reset the input so re-picking the SAME file after a rejection still fires change.
								event.target.value = "";
								if (picked) {
									void uploadLogo(picked);
								}
							}}
						/>
						<Hint>{m.account.brand.logoHint}</Hint>
					</Row>

					<Row>
						<FieldLabel id="brand-color-label">{m.account.brand.colorLabel}</FieldLabel>
						<ColorValue>{color}</ColorValue>
						<Hint>{m.account.brand.colorHint}</Hint>
					</Row>
					{/*
					  * ui#622: a curated palette replaced the native colour input outright — palette
					  * only, by owner decision. The readout above still prints the SAVED hex rather than
					  * the nearest swatch, which is what keeps an account branded before the palette
					  * existed honest: its colour shows here and in the preview while no swatch claims to
					  * be selected.
					  *
					  * The full 36 here, where the sender is configuring the account's own brand and every
					  * colour the api accepts should be reachable. Only the builder narrows the list
					  * (ui#1923).
					  */}
					<BrandColorPalette
						value={color}
						colors={BRAND_PALETTE}
						disabled={busy}
						labelledBy="brand-color-label"
						onPick={setColor}
					/>

					<Row>
						<FieldLabel>{m.account.brand.previewTitle}</FieldLabel>
					</Row>
					<PreviewBar style={{"--brand": color, "--brand-on": onColor} as React.CSSProperties}>
						{brand.logoUrl && <img src={brand.logoUrl} alt={m.account.brand.logoAlt}/>}
						<span>{m.account.brand.previewSample}</span>
					</PreviewBar>
					<Hint>{m.account.brand.snapshotNote}</Hint>
					{/* api#234: reset deletes the versions nothing was sent with and keeps the rest, so the
					    control says so next to itself rather than letting "Remove branding" imply erasure. */}
					<Hint>{m.account.brand.resetNote}</Hint>

					<Actions>
						<GhostButton disabled={busy || !canReset} onClick={() => void reset()}>
							{m.account.brand.reset}
						</GhostButton>
						<SaveButton disabled={busy || !canSave} onClick={() => void saveColor()}>
							{m.common.save}
						</SaveButton>
					</Actions>
				</CardMain>
			)}
		</Section>
	);
}
